import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Plus, Target, Wallet, PieChart, RefreshCw } from 'lucide-react';
import { dashboardAPI, portfolioAPI } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { formatCurrency, formatPercent } from '../utils/formatters';

/**
 * HomeScreen — Main dashboard showing portfolio summary, goals, and recent transactions.
 */
const HomeScreen = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [portfolioSummary, setPortfolioSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [dashRes, portRes] = await Promise.all([
        dashboardAPI.getData(),
        portfolioAPI.getSummary(),
      ]);

      setDashboardData(dashRes.data);
      setPortfolioSummary(portRes.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load dashboard data.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24">
        {/* Hero skeleton */}
        <div className="bg-gradient-to-br from-blue-600 to-blue-800 px-6 pt-8 pb-12 rounded-b-3xl">
          <div className="max-w-4xl mx-auto animate-pulse">
            <div className="h-3 bg-white/20 rounded-full w-24 mb-2" />
            <div className="h-7 bg-white/20 rounded-full w-36 mb-1" />
            <div className="h-3 bg-white/20 rounded-full w-48" />
          </div>
        </div>
        <div className="max-w-4xl mx-auto px-4 -mt-6 space-y-4">
          {/* Portfolio card skeleton */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 animate-pulse">
            <div className="h-3 bg-gray-200 rounded-full w-32 mb-5" />
            <div className="grid grid-cols-2 gap-6 mb-4">
              <div className="space-y-2">
                <div className="h-2.5 bg-gray-100 rounded-full w-20" />
                <div className="h-7 bg-gray-200 rounded-full w-28" />
              </div>
              <div className="space-y-2">
                <div className="h-2.5 bg-gray-100 rounded-full w-20" />
                <div className="h-7 bg-gray-200 rounded-full w-28" />
              </div>
            </div>
            <div className="h-16 bg-gray-100 rounded-xl mb-4" />
            <div className="grid grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-gray-100 rounded-lg" />
              ))}
            </div>
          </div>
          {/* Goals card skeleton */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 animate-pulse">
            <div className="h-3 bg-gray-200 rounded-full w-28 mb-5" />
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex justify-between">
                  <div className="h-4 bg-gray-100 rounded-full w-24" />
                  <div className="h-4 bg-gray-100 rounded-full w-8" />
                </div>
              ))}
            </div>
          </div>
          {/* Transactions card skeleton */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 animate-pulse">
            <div className="h-3 bg-gray-200 rounded-full w-36 mb-5" />
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-14 bg-gray-100 rounded-xl" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-700 mb-4">{error}</p>
          <button
            onClick={fetchData}
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
          >
            <RefreshCw className="w-4 h-4" /> Retry
          </button>
        </div>
      </div>
    );
  }

  const portfolio = portfolioSummary || {};
  const gainLoss = parseFloat(portfolio.total_gain_loss || 0);
  const gainLossClass = gainLoss >= 0 ? 'text-green-600' : 'text-red-600';
  const gainLossSign = gainLoss >= 0 ? '+' : '';

  const fmtInvested = formatCurrency(parseFloat(portfolio.total_invested || 0));
  const fmtCurrentValue = formatCurrency(parseFloat(portfolio.current_value || 0));
  const stackMetrics = fmtInvested.length > 10 || fmtCurrentValue.length > 10;

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-gradient-to-br from-blue-600 to-blue-800 text-white px-6 pt-8 pb-12 rounded-b-3xl shadow-lg">
        <div className="max-w-4xl mx-auto flex items-start justify-between">
          <div>
            <p className="text-blue-200 text-sm mb-1">Welcome back</p>
            <h1 className="text-2xl font-bold mb-0.5">
              {dashboardData?.user?.first_name || user?.first_name || 'Investor'}
            </h1>
            <p className="text-blue-200 text-sm">Track your investment goals</p>
          </div>
          <button
            onClick={fetchData}
            className="p-2 rounded-xl text-blue-300 hover:text-white hover:bg-white/10 transition mt-1"
            title="Refresh"
            aria-label="Refresh dashboard"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 -mt-6 space-y-4">
        {/* Portfolio Summary Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-gray-500 text-xs font-semibold uppercase tracking-wide mb-4 flex items-center gap-2">
            <Wallet className="w-4 h-4" /> Portfolio Overview
          </h2>
          <div className={`grid ${stackMetrics ? 'grid-cols-1 gap-3' : 'grid-cols-2 gap-6'} mb-4`}>
            <div className={stackMetrics ? 'flex items-baseline justify-between' : ''}>
              <p className="text-gray-500 text-xs mb-1">Total Invested</p>
              <p className={`${stackMetrics ? 'text-xl' : 'text-2xl'} font-bold text-gray-900`}>
                {fmtInvested}
              </p>
            </div>
            <div className={stackMetrics ? 'flex items-baseline justify-between' : ''}>
              <p className="text-gray-500 text-xs mb-1">Current Value</p>
              <p className={`${stackMetrics ? 'text-xl' : 'text-2xl'} font-bold text-gray-900`}>
                {fmtCurrentValue}
              </p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-4 mb-4">
            <p className="text-gray-500 text-xs mb-1">Unrealized Gain / Loss</p>
            <p className={`text-xl font-bold ${gainLossClass}`}>
              {gainLossSign}{formatCurrency(Math.abs(gainLoss))}
            </p>
            <p className={`text-sm font-medium ${gainLossClass}`}>
              {gainLossSign}{formatPercent(portfolio.total_gain_loss_percentage || 0)}
            </p>
          </div>

          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="bg-blue-50 rounded-xl p-3">
              <p className="text-gray-500 text-xs mb-1">Assets</p>
              <p className="text-xl font-bold text-blue-700">{portfolio.assets_count || 0}</p>
            </div>
            <div className="bg-blue-50 rounded-xl p-3">
              <p className="text-gray-500 text-xs mb-1">Brokers</p>
              <p className="text-xl font-bold text-blue-700">{portfolio.brokers_count || 0}</p>
            </div>
            <div className="bg-blue-50 rounded-xl p-3">
              <p className="text-gray-500 text-xs mb-1">Active Goals</p>
              <p className="text-xl font-bold text-blue-700">{portfolio.active_goals || 0}</p>
            </div>
          </div>
        </div>

        {/* Goals Summary Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-gray-500 text-xs font-semibold uppercase tracking-wide flex items-center gap-2">
              <Target className="w-4 h-4" /> Investment Goals
            </h2>
            <Link to="/goals" className="text-blue-600 text-xs font-medium hover:underline">
              View all
            </Link>
          </div>

          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600 text-sm">Active Goals</span>
              <span className="font-bold text-gray-900">{dashboardData?.goals?.active || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 text-sm">Completed</span>
              <span className="font-bold text-green-600">{dashboardData?.goals?.completed || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 text-sm">Total Saved</span>
              <span className="font-bold text-gray-900">
                {formatCurrency(parseFloat(dashboardData?.goals?.total_amount || 0))}
              </span>
            </div>
          </div>
        </div>

        {/* Recent Transactions Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-gray-500 text-xs font-semibold uppercase tracking-wide flex items-center gap-2">
              <TrendingUp className="w-4 h-4" /> Recent Transactions
            </h2>
            <Link to="/transactions" className="text-blue-600 text-xs font-medium hover:underline">
              View all
            </Link>
          </div>

          {dashboardData?.transactions?.recent?.length > 0 ? (
            <div className="space-y-2">
              {dashboardData.transactions.recent.map((tx) => (
                <div
                  key={tx.id}
                  className="flex justify-between items-center p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-gray-900">{tx.asset_symbol}</p>
                    <p className="text-xs text-gray-500 capitalize">
                      {tx.transaction_type_display || tx.transaction_type} &middot; {tx.quantity} units
                    </p>
                    <p className="text-xs text-gray-400">
                      {new Date(tx.transaction_date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-sm text-gray-900">
                      {formatCurrency(parseFloat(tx.total_amount))}
                    </p>
                    <p className="text-xs text-gray-400">{tx.broker_name}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <PieChart className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500 text-sm">No transactions yet</p>
              <Link to="/transactions" className="text-blue-600 text-sm hover:underline mt-1 block">
                Add your first transaction
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Floating Action Buttons */}
      <div className="fixed bottom-24 right-4 flex flex-col items-end gap-2.5">
        <Link
          to="/transactions"
          className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-full shadow-lg hover:bg-green-700 active:scale-95 transition text-sm font-semibold"
        >
          <Plus className="w-4 h-4" />
          New Trade
        </Link>
        <Link
          to="/goals"
          className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 active:scale-95 transition text-sm font-semibold"
        >
          <Target className="w-4 h-4" />
          New Goal
        </Link>
      </div>
    </div>
  );
};

export default HomeScreen;
